import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { FilterBar } from './FilterBar';
import { MovieGrid } from './MovieGrid';
import { OnboardingModal } from './OnboardingModal';
import { useDiscoverQuery } from '../hooks/useDiscoverQuery';
import { useMediaType } from '../contexts/MediaTypeContext';
import { useAuth } from '../contexts/AuthContext';
import { SORT_OPTIONS, CURRENT_YEAR, DEFAULT_FILTERS } from '../utils/constants';
import type { MediaItem, SortOption, Filters, MediaType } from '../types/movie';

// Parse filters from URL search params
function parseFiltersFromURL(searchParams: URLSearchParams): Partial<Filters> {
  const filters: Partial<Filters> = {};

  const genres = searchParams.get('genres');
  if (genres) filters.genres = genres.split(',').map(Number).filter(Boolean);

  const yearMin = searchParams.get('yearMin');
  const yearMax = searchParams.get('yearMax');
  if (yearMin || yearMax) {
    filters.yearRange = [
      yearMin ? parseInt(yearMin) : 1920,
      yearMax ? parseInt(yearMax) : CURRENT_YEAR,
    ];
  }

  const runtimeMin = searchParams.get('runtimeMin');
  const runtimeMax = searchParams.get('runtimeMax');
  if (runtimeMin || runtimeMax) {
    filters.runtimeRange = [
      runtimeMin ? parseInt(runtimeMin) : 0,
      runtimeMax ? parseInt(runtimeMax) : 300,
    ];
  }

  const ratingMin = searchParams.get('ratingMin');
  const ratingMax = searchParams.get('ratingMax');
  if (ratingMin || ratingMax) {
    filters.ratingRange = [
      ratingMin ? parseFloat(ratingMin) : 0,
      ratingMax ? parseFloat(ratingMax) : 10,
    ];
  }

  const rtMin = searchParams.get('rtMin');
  const rtMax = searchParams.get('rtMax');
  if (rtMin || rtMax) {
    filters.rtRatingRange = [
      rtMin ? parseInt(rtMin) : 0,
      rtMax ? parseInt(rtMax) : 100,
    ];
  }

  const tmdbMin = searchParams.get('tmdbMin');
  const tmdbMax = searchParams.get('tmdbMax');
  if (tmdbMin || tmdbMax) {
    filters.tmdbRatingRange = [
      tmdbMin ? parseFloat(tmdbMin) : 0,
      tmdbMax ? parseFloat(tmdbMax) : 10,
    ];
  }

  const ourMin = searchParams.get('ourMin');
  const ourMax = searchParams.get('ourMax');
  if (ourMin || ourMax) {
    filters.ourRatingRange = [
      ourMin ? parseFloat(ourMin) : 0,
      ourMax ? parseFloat(ourMax) : 10,
    ];
  }

  const languages = searchParams.get('languages');
  if (languages) filters.languages = languages.split(',');

  const sortBy = searchParams.get('sortBy');
  if (sortBy) filters.sortBy = sortBy as SortOption;

  const actor = searchParams.get('actor');
  if (actor) filters.actorSearch = actor.split(',');

  const director = searchParams.get('director');
  if (director) filters.directorSearch = director.split(',');

  // Popular only toggle (default is true, so only set if explicitly 'false')
  const popularOnly = searchParams.get('popularOnly');
  if (popularOnly === 'false') filters.popularOnly = false;

  // Released only toggle (default is true, so only set if explicitly 'false')
  const releasedOnly = searchParams.get('releasedOnly');
  if (releasedOnly === 'false') filters.releasedOnly = false;

  return filters;
}

// Parse media type from URL
function parseMediaTypeFromURL(searchParams: URLSearchParams): MediaType | null {
  const type = searchParams.get('type');
  if (type === 'tv' || type === 'movie') return type;
  return null;
}

function buildFiltersFromURL(searchParams: URLSearchParams): Filters {
  return {
    ...DEFAULT_FILTERS,
    ...parseFiltersFromURL(searchParams),
  };
}

function areFiltersEqual(a: Filters, b: Filters): boolean {
  const sortedNumbers = (arr: number[]) => [...arr].sort((x, y) => x - y);
  const sortedStrings = (arr: string[]) => [...arr].sort();
  const numberPairEqual = (x: [number, number], y: [number, number]) => x[0] === y[0] && x[1] === y[1];

  if (!numberPairEqual(a.yearRange, b.yearRange)) return false;
  if (!numberPairEqual(a.runtimeRange, b.runtimeRange)) return false;
  if (!numberPairEqual(a.ratingRange, b.ratingRange)) return false;
  if (!numberPairEqual(a.rtRatingRange, b.rtRatingRange)) return false;
  if (!numberPairEqual(a.tmdbRatingRange, b.tmdbRatingRange)) return false;
  if (!numberPairEqual(a.ourRatingRange, b.ourRatingRange)) return false;
  if (a.sortBy !== b.sortBy) return false;
  if (a.popularOnly !== b.popularOnly) return false;
  if (a.releasedOnly !== b.releasedOnly) return false;

  const genresA = sortedNumbers(a.genres);
  const genresB = sortedNumbers(b.genres);
  if (genresA.length !== genresB.length || genresA.some((v, i) => v !== genresB[i])) return false;

  const langsA = sortedStrings(a.languages);
  const langsB = sortedStrings(b.languages);
  if (langsA.length !== langsB.length || langsA.some((v, i) => v !== langsB[i])) return false;

  const actorsA = sortedStrings(a.actorSearch);
  const actorsB = sortedStrings(b.actorSearch);
  if (actorsA.length !== actorsB.length || actorsA.some((v, i) => v !== actorsB[i])) return false;

  const directorsA = sortedStrings(a.directorSearch);
  const directorsB = sortedStrings(b.directorSearch);
  if (directorsA.length !== directorsB.length || directorsA.some((v, i) => v !== directorsB[i])) return false;

  return true;
}

// Convert filters to URL search params
function filtersToURLParams(filters: Filters, mediaType: MediaType, searchQuery: string): URLSearchParams {
  const params = new URLSearchParams();

  // Always save media type if it's TV
  if (mediaType === 'tv') {
    params.set('type', 'tv');
  }

  if (filters.genres.length > 0) {
    params.set('genres', filters.genres.join(','));
  }

  if (filters.yearRange[0] > 1920 || filters.yearRange[1] < CURRENT_YEAR) {
    if (filters.yearRange[0] > 1920) params.set('yearMin', String(filters.yearRange[0]));
    if (filters.yearRange[1] < CURRENT_YEAR) params.set('yearMax', String(filters.yearRange[1]));
  }

  if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
    if (filters.runtimeRange[0] > 0) params.set('runtimeMin', String(filters.runtimeRange[0]));
    if (filters.runtimeRange[1] < 300) params.set('runtimeMax', String(filters.runtimeRange[1]));
  }

  if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
    if (filters.ratingRange[0] > 0) params.set('ratingMin', String(filters.ratingRange[0]));
    if (filters.ratingRange[1] < 10) params.set('ratingMax', String(filters.ratingRange[1]));
  }

  if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
    if (filters.rtRatingRange[0] > 0) params.set('rtMin', String(filters.rtRatingRange[0]));
    if (filters.rtRatingRange[1] < 100) params.set('rtMax', String(filters.rtRatingRange[1]));
  }

  if (filters.tmdbRatingRange[0] > 0 || filters.tmdbRatingRange[1] < 10) {
    if (filters.tmdbRatingRange[0] > 0) params.set('tmdbMin', String(filters.tmdbRatingRange[0]));
    if (filters.tmdbRatingRange[1] < 10) params.set('tmdbMax', String(filters.tmdbRatingRange[1]));
  }

  if (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) {
    if (filters.ourRatingRange[0] > 0) params.set('ourMin', String(filters.ourRatingRange[0]));
    if (filters.ourRatingRange[1] < 10) params.set('ourMax', String(filters.ourRatingRange[1]));
  }

  if (filters.languages.length > 0) {
    params.set('languages', filters.languages.join(','));
  }

  if (filters.sortBy !== DEFAULT_FILTERS.sortBy) {
    params.set('sortBy', filters.sortBy);
  }

  if (filters.actorSearch.length > 0) {
    params.set('actor', filters.actorSearch.join(','));
  }

  if (filters.directorSearch.length > 0) {
    params.set('director', filters.directorSearch.join(','));
  }

  // Only save popularOnly if it's false (true is default)
  if (filters.popularOnly === false) {
    params.set('popularOnly', 'false');
  }

  // Only save releasedOnly if it's false (true is default)
  if (filters.releasedOnly === false) {
    params.set('releasedOnly', 'false');
  }

  if (searchQuery) {
    params.set('q', searchQuery);
  }

  return params;
}

export function FilmFinder() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOpen, setSortOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const visibilityRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const { mediaType, setMediaType } = useMediaType();

  // Auth state (only used for onboarding)
  const { needsOnboarding, completeOnboarding } = useAuth();
  const showOnboarding = needsOnboarding;

  const handleOnboardingComplete = () => {
    completeOnboarding();
  };

  // Parse initial filters, search query, and media type from URL once.
  const [initialFilters] = useState<Partial<Filters>>(() => parseFiltersFromURL(searchParams));
  const [initialUrlMediaType] = useState<MediaType | null>(() => parseMediaTypeFromURL(searchParams));
  const [initialSearchQuery] = useState<string>(() => searchParams.get('q') || '');
  const hasInitializedMediaType = useRef(false);
  const skipNextUrlWriteRef = useRef(false);

  // Set media type from URL on mount
  useEffect(() => {
    if (!hasInitializedMediaType.current) {
      hasInitializedMediaType.current = true;
      if (initialUrlMediaType) {
        setMediaType(initialUrlMediaType);
      }
    }
  }, [setMediaType, initialUrlMediaType]);

  const effectiveMediaType = mediaType;
  const isTV = effectiveMediaType === 'tv';

  const {
    movies,
    loading,
    isFetchingNextPage,
    error,
    hasMore,
    totalResults,
    loadMore,
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
  } = useDiscoverQuery(initialFilters, effectiveMediaType, initialSearchQuery);

  // Refs to track current values for URL->state sync without re-triggering the effect
  const filtersRef = useRef(filters);
  const searchQueryRef = useRef(searchQuery);
  const mediaTypeRef = useRef(mediaType);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { mediaTypeRef.current = mediaType; }, [mediaType]);

  // Sync URL -> local state for browser back/forward and deep links.
  // Without this, local filters can overwrite popped URLs and break back navigation.
  // Uses refs for comparison to avoid re-running on every filter/slider change.
  useEffect(() => {
    let updatedFromUrl = false;

    const nextFilters = buildFiltersFromURL(searchParams);
    if (!areFiltersEqual(filtersRef.current, nextFilters)) {
      updatedFromUrl = true;
      setFilters(nextFilters);
    }

    const urlQuery = searchParams.get('q') || '';
    if (urlQuery !== searchQueryRef.current) {
      updatedFromUrl = true;
      setSearchQuery(urlQuery);
    }

    // Only sync media type when URL explicitly declares it.
    // This avoids implicit movie/tv resets that can cause URL ping-pong.
    const urlMediaType = parseMediaTypeFromURL(searchParams);
    if (urlMediaType && urlMediaType !== mediaTypeRef.current) {
      updatedFromUrl = true;
      setMediaType(urlMediaType);
    }

    // Prevent stale state->URL writes in the same commit cycle as URL->state sync.
    if (updatedFromUrl) {
      skipNextUrlWriteRef.current = true;
    }
  }, [searchParams, setFilters, setSearchQuery, setMediaType]);

  // Sync filters and media type to URL when they change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }

    const newParams = filtersToURLParams(filters, effectiveMediaType, searchQuery);
    const currentQueryString = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;
    if (currentQueryString === newParams.toString()) {
      return;
    }

    // Use replace to avoid creating new history entries on every filter change
    setSearchParams(newParams, { replace: true });
  }, [filters, searchQuery, effectiveMediaType, location.search, setSearchParams]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
      if (visibilityRef.current && !visibilityRef.current.contains(event.target as Node)) {
        setVisibilityOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMovieClick = (item: MediaItem) => {
    const route = item.media_type === 'tv' ? `/tv/${item.id}` : `/movie/${item.id}`;
    navigate(route);
  };

  const handleSortChange = (sortBy: SortOption) => {
    setFilters({ ...filters, sortBy });
    setSortOpen(false);
  };

  const activeSortLabel = SORT_OPTIONS.find(o => o.value === filters.sortBy)?.label || 'Most Popular';

  // Visibility button label
  const visibilityLabel = filters.popularOnly && filters.releasedOnly
    ? 'Released + Popular'
    : filters.popularOnly
      ? 'Popular'
      : filters.releasedOnly
        ? 'Released'
        : 'All';

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Filter Bar (includes Active Filters) */}
      <FilterBar filters={filters} onChange={setFilters} mediaType={effectiveMediaType} />

      {/* Results info with Sort dropdown */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between">
          <p className="text-slate-400 text-sm">
            {loading && movies.length === 0 ? 'Loading...' : `${totalResults.toLocaleString()} ${isTV ? 'TV shows' : 'movies'} found`}
          </p>
          <div className="flex items-center gap-4">
            {error && (
              <p className="text-amber-400 text-sm">
                Showing sample data
              </p>
            )}
            {/* Visibility Dropdown */}
            <div ref={visibilityRef} className="relative">
              <button
                onClick={() => { setVisibilityOpen(!visibilityOpen); setSortOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-slate-300 hover:text-white hover:ring-white/[0.12] transition-all"
              >
                <span className="text-sm">Visibility: <span className="text-cyan-400 font-medium">{visibilityLabel}</span></span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${visibilityOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {visibilityOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-xl shadow-xl shadow-black/40 z-50 overflow-hidden p-3 space-y-3">
                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <div>
                      <span className="text-sm text-slate-200">Popular only</span>
                      <p className="text-xs text-slate-500">Filter low-signal titles</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={filters.popularOnly}
                        onChange={(e) => setFilters({ ...filters, popularOnly: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-600 rounded-full peer peer-checked:bg-teal-500 transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <div>
                      <span className="text-sm text-slate-200">Released only</span>
                      <p className="text-xs text-slate-500">Hide upcoming titles</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={filters.releasedOnly}
                        onChange={(e) => setFilters({ ...filters, releasedOnly: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-600 rounded-full peer peer-checked:bg-teal-500 transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
                    </div>
                  </label>
                </div>
              )}
            </div>
            {/* Sort Dropdown */}
            <div ref={sortRef} className="relative">
              <button
                onClick={() => { setSortOpen(!sortOpen); setVisibilityOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-slate-300 hover:text-white hover:ring-white/[0.12] transition-all"
              >
                <span className="text-sm">Sort: <span className="text-cyan-400 font-medium">{activeSortLabel}</span></span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-xl shadow-xl shadow-black/40 z-50 overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                          filters.sortBy === option.value
                            ? 'bg-cyan-500/20 text-cyan-400'
                            : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Movie Grid */}
      <main className="px-4 pb-8">
        <MovieGrid
          movies={movies}
          loading={loading}
          isFetchingNextPage={isFetchingNextPage}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onMovieClick={handleMovieClick}
          mediaType={effectiveMediaType}
        />
      </main>

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
}
